import React, { useEffect, useState } from "react";
import {
  Box,
  Center,
  Avatar,
  HStack,
  Text,
  Flex,
  VStack,
  Button,
  Heading,
} from "@chakra-ui/react";
import { Student } from "../../../services/Dtos";
import StudentTopMenu from "./StudentTopMenu";
import AddStudentModal from "./AddStudentModal";
import StudentInfoPanel from "./StudentInfoPanel";

import { useStudentsContext } from "../../../Contexts/StudentsContext";
import { studentStatusMap, editorPolicy, genderMap } from "../../../services/constants";
import { mapClass } from "../../../services/Utils";
import AddGuardianModal from "./AddGuardianModal";
import Empty from "../../CustomUi/Empty";
import Loader from "../../CustomUi/Loader";
import CustomAlert from "../../CustomUi/CustomAlert";
import { useUserContext } from "../../../Contexts/userContext";
import { useSchoolsContext } from "../../../Contexts/schoolContext";
import { useNavigate } from "react-router-dom";
import BulkImportFeedbackModal from "../../CustomUi/BulkImportFeedbackModal";
import FileUploadModal from "../../CustomUi/FileUploadModal";
import { useDisclosure } from "@chakra-ui/react";
import { useStyles } from "../../../hooks/useStyles";
import useThemeColors from "../../../hooks/useThemeColors";
import { useStudentService } from "../../../hooks/Domain/useStudentService";
import { useBreakPoint } from "../../../hooks/useBreakPoint";

const Students: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortDate, setSortDate] = useState(true);
  const colors = useThemeColors();
  const scrollColor = colors.border.secondary;
  const tHeadColor = colors.background.tertiary;
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<number | null>(0); // Default to Active status (0)
  const [studentToEdit, setStudentToEdit] = useState<Student | null>(null);
  const { scrollBar } = useStyles();
  const { user } = useUserContext();
  const [guardianToEdit, setGuardianToEdit] = useState<any>(null);
  const [openGuardianModal, setOpenGuardianModal] = useState(false);
  const [openStudentModal, setOpenStudentModal] = useState(false);
  const [openBulkStudentModal, setOpenBulkStudentModal] = useState(false);
  const [importFeedback, setImportFeedback] = useState<any>(null);
  const {
    open: isFeedbackOpen,
    onOpen: onFeedbackOpen,
    onClose: onFeedbackClose,
  } = useDisclosure();

  const {
    students,
    isLoading: studentsLoading,
    error: studentsError,
    selectedStudent,
    setSelectedStudent,
  } = useStudentsContext();

  const { classes } = useSchoolsContext();
  const [classMap, setClassMap] = useState<{ [key: string]: string }>({});
  const { isMobile } = useBreakPoint();

  // Initialize student service
  const {
    bulkImportStudents,
    downloadTemplate,
    isLoading: isActionLoading,
  } = useStudentService();

  useEffect(() => {
    if (classes) {
      const map = mapClass(classes);
      setClassMap(map);
    }
  }, [classes]);

  if (studentsLoading) {
    return <Loader />;
  }

  if (studentsError) {
    return (
      <Center height="100%">
        <CustomAlert message={studentsError} status="error" />
        <Button>Retry</Button>
      </Center>
    );
  }

  const justFilteredStudents = students?.filter((student) => {
    const fullName = `${student.firstName.toLowerCase()} ${student.middleName?.toLowerCase()} ${student.lastName.toLowerCase()}`;
    return (
      fullName.includes(searchQuery.toLowerCase()) &&
      (selectedClass === "" || student.currentClassId === selectedClass) &&
      (selectedStatus === null || student.status === selectedStatus)
    );
  });

  const filteredStudents = sortDate
    ? justFilteredStudents.reverse()
    : justFilteredStudents;

  const handleSortDate = () => {
    setSortDate((prev) => !prev);
  };

  const handleRowClick = (student: Student) => {
    if (selectedStudent === student) {
      setSelectedStudent(null);
    } else {
      setSelectedStudent(student);
    }
  };

  const handleAddOpenGuardian = () => {
    setGuardianToEdit(null);
    setOpenGuardianModal(true);
  };

  const handleEditGuardian = (guardianInBox: any) => {
    setGuardianToEdit(guardianInBox);
    setOpenGuardianModal(true);
  };

  const handleAddStudent = () => {
    setStudentToEdit(null);
    setOpenStudentModal(true);
  };

  const handleEditStudent = (studentToEdit: Student) => {
    setStudentToEdit(studentToEdit);
    setOpenStudentModal(true);
  };

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setSearchQuery(e.target.value);
  };

  const handleClassChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setSelectedClass(e.target.value);
  };

  const handleStatusChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setSelectedStatus(e.target.value === "" ? null : parseInt(e.target.value));
  };

  function firstFiveClassStudents(student: Student): Student[] {
    return students
      .filter((s) => s.currentClassId === student.currentClassId)
      .slice(0, 5);
  }

  function classCount(student: Student) {
    return students.filter((s) => s.currentClassId === student.currentClassId)
      .length;
  }

  function handleSameClass(studentClass: string) {
    selectedClass === studentClass
      ? setSelectedClass("")
      : setSelectedClass(studentClass);
  }

  const handleViewResults = (student: Student) => {
    // Set the selected class in localStorage for the Results page to pick up
    localStorage.setItem("selectedClassId", student.currentClassId);
    localStorage.setItem("chosenStudentId", student.id);
    // Navigate to the results page
    navigate("/results");
  };

  const handleBulkAddOpen = () => {
    setOpenBulkStudentModal(true);
  };

  const handleDownloadTemplate = async () => {
    await downloadTemplate();
  };

  const handleBulkUpload = async (file: File) => {
    setOpenBulkStudentModal(false);
    onFeedbackOpen();

    const response = await bulkImportStudents(file);
    if (response.result.succeeded) {
      setImportFeedback(response.result.data);
    }
  };

  return (
    <VStack flex={1} overflow="hidden" h={"full"}>
      <StudentTopMenu
        searchQuery={searchQuery}
        selectedClass={selectedClass}
        selectedStatus={selectedStatus}
        handleClassChange={handleClassChange}
        handleStatusChange={handleStatusChange}
        handleSearchChange={handleSearchChange}
        classes={classes}
        onOpen={handleAddStudent}
        sortDate={sortDate}
        handleSortDate={handleSortDate}
        handleBulkAddOpen={handleBulkAddOpen}
        handleDownloadTemplate={handleDownloadTemplate}
        isActionLoading={isActionLoading}
      />
      <AddStudentModal
        isOpen={openStudentModal}
        onClose={() => setOpenStudentModal(false)}
        openGuardianForm={handleAddOpenGuardian}
        studentToEdit={studentToEdit}
        selectedStudentId={selectedStudent?.id || ""}
      />

      <AddGuardianModal
        isOpen={openGuardianModal}
        onClose={() => setOpenGuardianModal(false)}
        guardianToEdit={guardianToEdit}
      />
      <FileUploadModal
        isOpen={openBulkStudentModal}
        onClose={() => setOpenBulkStudentModal(false)}
        onUpload={handleBulkUpload}
        title="Bulk Add Students"
        description="Drag & drop or click to select Excel or CSV file"
        isUploading={isActionLoading}
      />
      <BulkImportFeedbackModal
        isOpen={isFeedbackOpen}
        onClose={onFeedbackClose}
        feedback={importFeedback}
        isUploading={isActionLoading}
        title="Import Students"
        loadingText="Uploading and processing students..."
      />
      {students.length !== 0 ? (
        isMobile && selectedStudent ? (
          <Box
            w="full"
            h="full"
            p={2}
            overflow={"hidden"}
            display={"flex"}
            flexDir={"column"}
          >
            <Button
              variant="outline"
              m={2}
              onClick={() => setSelectedStudent(null)}
            >
              Back
            </Button>
            <StudentInfoPanel
              selectedStudent={selectedStudent}
              user={user}
              classMap={classMap}
              handleEditStudent={handleEditStudent}
              handleSameClass={handleSameClass}
              classCount={classCount}
              firstFiveClassStudents={firstFiveClassStudents}
              handleAddOpenGuardian={handleAddOpenGuardian}
              handleEditGuardian={handleEditGuardian}
              handleViewResults={handleViewResults}
              editorPolicy={editorPolicy}
              altColor={colors.background.secondary}
              brandTransparentColor={colors.background.secondary}
              greenTransparentColor={colors.background.secondary}
              scrollColor={scrollColor}
              studentStatusMap={studentStatusMap}
              accentColor={colors.schoolAccent.primary}
            />
          </Box>
        ) : (
          <Flex
            w={"full"}
            flex={1}
            pt={4}
            overflow="hidden"
            h={"full"}
            direction={{ base: "column", lg: "row" }}
          >
            <Box
              flex={{ base: 1, md: 1, lg: 2, xl: 2.5 }}
              overflow={"hidden"}
              h={"full"}
              display="flex"
              flexDirection="column"
            >
              {/* Modern Table Header */}
              <Box
                bg={colors.background.secondary}
                borderRadius="lg"
                shadow="sm"
                borderWidth="1px"
                borderColor={colors.border.primary}
                overflow="hidden"
                display="flex"
                flexDirection="column"
                h="full"
              >
                <Box
                  bg={tHeadColor}
                  px={5}
                  py={3}
                  borderBottom="1px solid"
                  borderBottomColor={colors.border.primary}
                >
                  <HStack justify="space-between" align="center">
                    <Heading size="sm">
                      Students ({filteredStudents?.length || 0})
                    </Heading>
                    <Text fontSize="xs" color={colors.text.secondary}>
                      Select student to view details
                    </Text>
                  </HStack>
                </Box>

                {/* Student List */}
                <Box overflow={"auto"} flex="1" css={scrollBar}>
                  <VStack gap={0} align="stretch">
                    {filteredStudents &&
                      filteredStudents.map((student, index) => (
                        <Box
                          key={student.id}
                          px={4}
                          py={2}
                          borderBottomWidth={
                            index < filteredStudents.length - 1 ? "1px" : "none"
                          }
                          _hover={{
                            backgroundColor:
                              selectedStudent?.id === student.id
                                ? colors.schoolAccent.focus
                                : colors.schoolAccent.hover,
                            transform:
                              selectedStudent?.id !== student.id
                                ? "translateX(4px)"
                                : "none",
                          }}
                          backgroundColor={
                            selectedStudent?.id === student.id
                              ? colors.schoolAccent.focus
                              : "transparent"
                          }
                          cursor="pointer"
                          transition="all 0.2s ease"
                          position="relative"
                          onClick={() => handleRowClick(student)}
                          _before={
                            selectedStudent?.id === student.id
                              ? {
                                  content: '""',
                                  position: "absolute",
                                  left: 0,
                                  top: 0,
                                  bottom: 0,
                                  width: "3px",
                                  bg: colors.schoolAccent.icon,
                                }
                              : {}
                          }
                        >
                          <HStack gap={3} align="center">
                            {/* Avatar and Name */}
                            <HStack gap={2} flex={2} minW="180px">
                              <Avatar.Root
                                size="xs"
                                ring={2}
                                ringColor={
                                  selectedStudent?.id === student.id
                                    ? colors.schoolAccent.primary
                                    : colors.border.secondary
                                }
                                shadow="sm"
                              >
                                <Avatar.Image src={student.avatarUrl} />
                                <Avatar.Fallback
                                  name={`${student.firstName} ${student.lastName}`}
                                />
                              </Avatar.Root>
                              <VStack gap={0} align="start">
                                <Text
                                  fontWeight="semibold"
                                  fontSize="sm"
                                  color={colors.text.primary}
                                  lineHeight="shorter"
                                >
                                  {`${student.firstName} ${student.middleName} ${student.lastName}`}
                                </Text>
                                <Text
                                  fontSize="xs"
                                  color={colors.text.secondary}
                                  fontWeight="medium"
                                >
                                  ID: {student.publicId}
                                </Text>
                              </VStack>
                            </HStack>

                            {/* Class */}
                            <Box flex={1} minW="100px">
                              <VStack gap={0} align="start">
                                <Text
                                  fontSize="xs"
                                  color={colors.text.secondary}
                                >
                                  CLASS
                                </Text>
                                <Text
                                  fontWeight="semibold"
                                  fontSize="sm"
                                  color={colors.text.primary}
                                >
                                  {classMap[student.currentClassId] ||
                                    "Unknown"}
                                </Text>
                              </VStack>
                            </Box>

                            {/* Gender */}
                            <Box flex={1} minW="80px">
                              <VStack gap={0} align="start">
                                <Text
                                  fontSize="xs"
                                  color={colors.text.secondary}
                                >
                                  GENDER
                                </Text>
                                <Text
                                  fontWeight="semibold"
                                  fontSize="sm"
                                  color={colors.text.primary}
                                >
                                  {genderMap[student.gender]}
                                </Text>
                              </VStack>
                            </Box>
                          </HStack>
                        </Box>
                      ))}
                  </VStack>
                </Box>
              </Box>
            </Box>
            {selectedStudent && (
              <StudentInfoPanel
                selectedStudent={selectedStudent}
                user={user}
                classMap={classMap}
                handleEditStudent={handleEditStudent}
                handleSameClass={handleSameClass}
                classCount={classCount}
                firstFiveClassStudents={firstFiveClassStudents}
                handleAddOpenGuardian={handleAddOpenGuardian}
                handleEditGuardian={handleEditGuardian}
                handleViewResults={handleViewResults}
                editorPolicy={editorPolicy}
                altColor={colors.background.secondary}
                brandTransparentColor={colors.background.secondary}
                greenTransparentColor={colors.background.secondary}
                scrollColor={scrollColor}
                studentStatusMap={studentStatusMap}
                accentColor={colors.schoolAccent.primary}
              />
            )}
          </Flex>
        )
      ) : (
        <Empty message="Start by adding students" />
      )}
    </VStack>
  );
};

export default Students;
